import React, {useCallback, useMemo, useState} from "react";
import {Col, FormControl, FormGroup, FormLabel, Row, InputGroup} from "react-bootstrap";
import {defineMessages, FormattedMessage} from "react-intl";
import {TierConditionTypeInfo} from "../../shared/api/loyaltyApi";
import {TierConditionType} from "../../../gen/witbooking/loyalty/shared/tier_pb";
import {getCurrencyCodeSymbol} from "../helper";
import {TierLevelUpgradeTransactionAmount} from "../index";
import {FormDefaultContainer} from "../styles";

interface LoyaltyCustomerTierUpgradeFormProps {
    tierConditionType: TierConditionTypeInfo;
    transactionAmount: TierLevelUpgradeTransactionAmount;
    setTransactionAmount: (data: TierLevelUpgradeTransactionAmount) => void;
    isInvalid: (invalid: boolean) => void;
}

const messages = defineMessages({
    addAmount: {
        id: "loyaltyCustomerHistorical.addAmount",
        defaultMessage: "Add amount",
    },
    addReservation: {
        id: "loyaltyCustomerHistorical.addReservation",
        defaultMessage: "Add reservation",
    },
    spentAmount: {
        id: "loyaltyCustomerHistorical.validateError.spentAmount",
        defaultMessage: "The amount must be higher than 0",
    },
    transactionsAmount: {
        id: "loyaltyCustomerHistorical.validateError.transactionsAmount",
        defaultMessage: "The number must be higher than 0",
    },
});

function LoyaltyCustomerTierUpgradeForm(props: LoyaltyCustomerTierUpgradeFormProps) {
    const [isInvalidTransactionNumber, setIsInvalidTransactionNumber] = useState(false);
    const [isInvalidTransactionSpentAmount, setIsInvalidTransactionSpentAmount] = useState(false);

    const isSpentAmountCondition = useMemo(() => {
        return props.tierConditionType === TierConditionType.SPENT_AMOUNT
    }, [props.tierConditionType]);

    const isTransactionAmountCondition = useMemo(() => {
        return props.tierConditionType === TierConditionType.TRANSACTIONS_AMOUNT
    }, [props.tierConditionType]);

    const validateTransactionNumber = useCallback((value: number) => {
        const isInvalid = value <= 0;
        setIsInvalidTransactionNumber(isInvalid);
        props.isInvalid(isInvalid);
    }, [props]);

    const validateTransactionSpentAmount = useCallback((value: number) => {
        const isInvalid = value <= 0;
        setIsInvalidTransactionSpentAmount(isInvalid);
        props.isInvalid(isInvalid);
    }, [props]);

    const handleOnlyNumbers = useCallback((e: any) => !/[0-9]/.test(e.key) && e.preventDefault(), []);

    const handleOnChangeTransactionNumber = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = Number(event.currentTarget.value);
        validateTransactionNumber(value);
        props.setTransactionAmount({
            transactionNumber: value,
            spentAmount: {
                amount: 0,
                currency: "EUR"
            }
        });
    }, [props, validateTransactionNumber]);

    const handleOnChangeSpentAmount = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = Number(event.currentTarget.value);
        validateTransactionSpentAmount(value);
        props.setTransactionAmount({
            transactionNumber: 0,
            spentAmount: {
                amount: value,
                currency: "EUR"
            }
        });
    }, [props, validateTransactionSpentAmount]);

    return (
        <FormDefaultContainer>
            <FormGroup as={Row} className="mb-3">
                <FormLabel column sm={6}><FormattedMessage {...messages.addAmount} /></FormLabel>
                <Col sm={6}>
                    <InputGroup>
                        <InputGroup.Text id="basic-currency">{getCurrencyCodeSymbol()}</InputGroup.Text>
                        <FormControl
                            aria-describedby="basic-currency"
                            disabled={isTransactionAmountCondition}
                            placeholder={isTransactionAmountCondition ? "0" : ""}
                            onChange={handleOnChangeSpentAmount}
                            type="number"
                            min={1}
                            isInvalid={!isTransactionAmountCondition && isInvalidTransactionSpentAmount}
                            onKeyPress={handleOnlyNumbers}
                        />
                        <FormControl.Feedback type="invalid">
                            <FormattedMessage {...messages.spentAmount} />
                        </FormControl.Feedback>
                    </InputGroup>
                </Col>
            </FormGroup>
            <FormGroup as={Row} className="mb-3">
                <FormLabel column sm={6}><FormattedMessage {...messages.addReservation} /></FormLabel>
                <Col sm={6}>
                    <FormControl
                        disabled={isSpentAmountCondition}
                        placeholder={isSpentAmountCondition ? "0" : ""}
                        onChange={handleOnChangeTransactionNumber}
                        type="number"
                        min={1}
                        isInvalid={!isSpentAmountCondition && isInvalidTransactionNumber}
                    />
                    <FormControl.Feedback type="invalid">
                        <FormattedMessage {...messages.transactionsAmount} />
                    </FormControl.Feedback>
                </Col>
            </FormGroup>
        </FormDefaultContainer>
    )
}

export default LoyaltyCustomerTierUpgradeForm;